import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, DollarSign, Plus, Edit2, Trash2, X, Loader2, Receipt, ArrowDownCircle, ArrowUpCircle, CalendarDays, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";

const CATEGORIAS = ["compras", "personal", "renta", "servicios", "transporte", "decoracion", "marketing", "impuestos", "otros"];

const Finanzas = () => {
  const navigate = useNavigate();
  const [resumen, setResumen] = useState(null);
  const [porEvento, setPorEvento] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [vista, setVista] = useState("eventos"); // "eventos" | "gastos"
  const [gastos, setGastos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mes, setMes] = useState(new Date().toISOString().slice(0, 7));
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ evento_id: "", categoria: "compras", proveedor: "", descripcion: "", monto: 0, fecha: "", metodo_pago: "transferencia", referencia: "" });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r, g, pe, ev] = await Promise.all([
        axios.get(`${API}/finanzas/resumen?mes=${mes}`),
        axios.get(`${API}/gastos`),
        axios.get(`${API}/finanzas/por-evento`),
        axios.get(`${API}/eventos`),
      ]);
      setResumen(r.data);
      setGastos(g.data || []);
      setPorEvento(pe.data);
      const evs = Array.isArray(ev.data) ? ev.data : ev.data?.eventos || [];
      setEventos(evs.filter(e => !["cancelado", "cancelada"].includes(e.estatus)));
    } catch (e) { toast.error("Error al cargar"); }
    finally { setLoading(false); }
  }, [mes]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const abrirNuevo = () => {
    setEditando(null);
    setForm({ evento_id: "", categoria: "compras", proveedor: "", descripcion: "", monto: 0, fecha: new Date().toISOString().slice(0, 10), metodo_pago: "transferencia", referencia: "" });
    setShowModal(true);
  };
  const abrirEdit = (g) => { setEditando(g); setForm({ ...g }); setShowModal(true); };
  const guardar = async () => {
    if (!form.monto || form.monto <= 0) { toast.error("Monto inválido"); return; }
    if (!form.descripcion) { toast.error("Descripción requerida"); return; }
    try {
      if (editando) {
        await axios.put(`${API}/gastos/${editando.gasto_id}`, form);
        toast.success("Gasto actualizado");
      } else {
        await axios.post(`${API}/gastos`, form);
        toast.success("Gasto registrado");
      }
      setShowModal(false);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const eliminar = async (g) => {
    if (!window.confirm(`¿Eliminar gasto "${g.descripcion}" por $${g.monto.toLocaleString("es-MX")}?`)) return;
    try { await axios.delete(`${API}/gastos/${g.gasto_id}`); toast.success("Eliminado"); fetchAll(); }
    catch { toast.error("Error al eliminar"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--brand-primary)" }} /></div>;

  return (
    <div className="max-w-6xl mx-auto pb-12">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--brand-primary)" }}>Finanzas</p>
          <h1 className="font-display text-4xl md:text-5xl leading-none" style={{ color: "var(--brand-text)" }}>Ingresos & Egresos</h1>
          <p className="text-sm mt-2" style={{ color: "var(--brand-text-muted)" }}>Utilidad real por mes: ingresos cobrados menos egresos registrados.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="month" value={mes} onChange={(e) => setMes(e.target.value)} className="eo-input w-auto" data-testid="filter-mes" />
          <button onClick={abrirNuevo} className="eo-btn eo-btn-primary" data-testid="btn-nuevo-gasto"><Plus className="w-4 h-4" /> Registrar gasto</button>
        </div>
      </div>

      {/* KPIs */}
      {resumen && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Kpi label="Ingresos cobrados" value={`$${resumen.ingresos.toLocaleString("es-MX", { maximumFractionDigits: 0 })}`} icon={ArrowUpCircle} bg="var(--brand-success-soft)" color="var(--brand-success)" />
          <Kpi label="Egresos / gastos" value={`$${resumen.egresos.toLocaleString("es-MX", { maximumFractionDigits: 0 })}`} icon={ArrowDownCircle} bg="var(--brand-danger-soft)" color="var(--brand-danger)" />
          <Kpi label="Utilidad" value={`$${resumen.utilidad.toLocaleString("es-MX", { maximumFractionDigits: 0 })}`} icon={DollarSign} bg="var(--brand-primary-soft)" color="var(--brand-primary-dark)" />
          <Kpi label="Margen" value={`${resumen.margen}%`} icon={resumen.margen >= 30 ? TrendingUp : TrendingDown} bg="var(--brand-accent-soft)" color="#8C6735" />
        </div>
      )}

      {/* Toggle vistas */}
      <div className="flex gap-1 mb-4 p-1 rounded-full w-fit" style={{ background: "var(--brand-surface-muted)" }}>
        <button onClick={() => setVista("eventos")} className={`px-4 py-1.5 rounded-full text-xs font-medium transition ${vista === "eventos" ? "shadow-sm" : ""}`}
          style={vista === "eventos" ? { background: "var(--brand-surface-card)", color: "var(--brand-primary)" } : { color: "var(--brand-text-muted)" }}
          data-testid="vista-por-evento">Por evento</button>
        <button onClick={() => setVista("gastos")} className={`px-4 py-1.5 rounded-full text-xs font-medium transition ${vista === "gastos" ? "shadow-sm" : ""}`}
          style={vista === "gastos" ? { background: "var(--brand-surface-card)", color: "var(--brand-primary)" } : { color: "var(--brand-text-muted)" }}
          data-testid="vista-gastos">Gastos generales</button>
      </div>

      {/* ===== FINANZAS POR EVENTO ===== */}
      {vista === "eventos" && (
        <div className="eo-card overflow-hidden mb-6">
          <div className="px-5 py-4 flex items-center justify-between flex-wrap gap-2" style={{ borderBottom: "1px solid var(--brand-border)" }}>
            <h3 className="font-display text-lg" style={{ color: "var(--brand-text)" }}>Finanzas por evento ({porEvento?.eventos?.length || 0})</h3>
            {porEvento?.totales && (
              <p className="text-xs" style={{ color: "var(--brand-text-muted)" }}>
                Por cobrar: <b style={{ color: "var(--brand-danger)" }}>${porEvento.totales.por_cobrar.toLocaleString("es-MX")}</b> ·
                Utilidad global: <b style={{ color: "var(--brand-success)" }}>${porEvento.totales.utilidad.toLocaleString("es-MX")}</b>
              </p>
            )}
          </div>
          {!porEvento?.eventos?.length ? (
            <div className="p-12 text-center">
              <CalendarDays className="w-10 h-10 mx-auto mb-2" style={{ color: "var(--brand-text-muted)", opacity: 0.4 }} />
              <p className="text-sm" style={{ color: "var(--brand-text-muted)" }}>Aún no hay eventos con movimientos financieros</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-left" style={{ color: "var(--brand-text-muted)", borderBottom: "1px solid var(--brand-border)" }}>
                    <th className="px-4 py-2">Evento</th>
                    <th className="px-3 py-2 text-right">Contratado</th>
                    <th className="px-3 py-2 text-right">Pagado</th>
                    <th className="px-3 py-2 text-right">Saldo</th>
                    <th className="px-3 py-2 text-right">Egresos</th>
                    <th className="px-3 py-2 text-right">Utilidad</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {porEvento.eventos.map(f => (
                    <tr key={f.evento_id} className="hover:bg-[var(--brand-surface-muted)] cursor-pointer" style={{ borderBottom: "1px solid var(--brand-border)" }}
                      onClick={() => navigate(`/panel/eventos/${f.evento_id}?tab=finanzas`)} data-testid={`fin-evento-${f.evento_id}`}>
                      <td className="px-4 py-2.5">
                        <p className="font-medium truncate max-w-[220px]" style={{ color: "var(--brand-text)" }}>{f.nombre}</p>
                        <p className="text-[11px]" style={{ color: "var(--brand-text-muted)" }}>{f.fecha || "sin fecha"}</p>
                      </td>
                      <td className="px-3 py-2.5 text-right" style={{ color: "var(--brand-text)" }}>${f.total.toLocaleString("es-MX")}</td>
                      <td className="px-3 py-2.5 text-right" style={{ color: "var(--brand-success)" }}>${f.pagado.toLocaleString("es-MX")}</td>
                      <td className="px-3 py-2.5 text-right" style={{ color: f.saldo > 0 ? "var(--brand-danger)" : "var(--brand-text-muted)" }}>${f.saldo.toLocaleString("es-MX")}</td>
                      <td className="px-3 py-2.5 text-right" style={{ color: "var(--brand-text)" }}>${f.egresos.toLocaleString("es-MX")}</td>
                      <td className="px-3 py-2.5 text-right font-medium" style={{ color: f.utilidad >= 0 ? "var(--brand-success)" : "var(--brand-danger)" }}>${f.utilidad.toLocaleString("es-MX")}</td>
                      <td className="px-2 py-2.5"><ExternalLink className="w-3.5 h-3.5" style={{ color: "var(--brand-text-muted)" }} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Por categoría */}
      {vista === "gastos" && resumen?.por_categoria?.length > 0 && (
        <div className="eo-card p-5 mb-6">
          <h3 className="font-display text-lg mb-3" style={{ color: "var(--brand-text)" }}>Egresos por categoría · {mes}</h3>
          <div className="space-y-2">
            {resumen.por_categoria.map(c => (
              <div key={c.categoria} className="flex items-center gap-3">
                <span className="text-xs uppercase tracking-wider w-28 shrink-0" style={{ color: "var(--brand-text-muted)" }}>{c.categoria}</span>
                <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: "var(--brand-surface-muted)" }}>
                  <div className="h-full" style={{ width: `${Math.min(100, (c.monto / resumen.egresos) * 100)}%`, background: "var(--brand-primary)" }} />
                </div>
                <span className="text-sm font-medium w-24 text-right" style={{ color: "var(--brand-text)" }}>${c.monto.toLocaleString("es-MX")}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabla gastos */}
      {vista === "gastos" && (
      <div className="eo-card overflow-hidden">
        <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--brand-border)" }}>
          <h3 className="font-display text-lg" style={{ color: "var(--brand-text)" }}>Historial de gastos ({gastos.length})</h3>
        </div>
        {gastos.length === 0 ? (
          <div className="p-12 text-center">
            <Receipt className="w-10 h-10 mx-auto mb-2" style={{ color: "var(--brand-text-muted)", opacity: 0.4 }} />
            <p className="text-sm" style={{ color: "var(--brand-text-muted)" }}>Sin gastos registrados</p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--brand-border)" }}>
            {gastos.map(g => (
              <div key={g.gasto_id} className="p-4 flex items-center gap-3 hover:bg-[var(--brand-surface-muted)]" data-testid={`gasto-${g.gasto_id}`}>
                <span className="px-2.5 py-1 rounded-full text-[10px] font-medium uppercase tracking-wider shrink-0" style={{ background: "var(--brand-surface-muted)", color: "var(--brand-text-muted)" }}>{g.categoria}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: "var(--brand-text)" }}>{g.descripcion}</p>
                  <p className="text-[11px]" style={{ color: "var(--brand-text-muted)" }}>{g.fecha} · {g.proveedor || "—"} · {g.metodo_pago}{g.evento_id ? ` · 📅 ${(eventos.find(e => e.evento_id === g.evento_id)?.nombre) || "evento"}` : ""}</p>
                </div>
                <span className="font-display text-lg" style={{ color: "var(--brand-danger)" }}>-${(g.monto || 0).toLocaleString("es-MX")}</span>
                <button onClick={() => abrirEdit(g)} className="p-2 rounded-lg hover:bg-[var(--brand-primary-soft)]" data-testid={`edit-${g.gasto_id}`}><Edit2 className="w-4 h-4" style={{ color: "var(--brand-primary)" }} /></button>
                <button onClick={() => eliminar(g)} className="p-2 rounded-lg hover:bg-red-50" data-testid={`del-${g.gasto_id}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="w-full max-w-md rounded-3xl overflow-hidden" style={{ background: "var(--brand-surface-card)" }} onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--brand-border)" }}>
              <h3 className="font-display text-xl" style={{ color: "var(--brand-text)" }}>{editando ? "Editar gasto" : "Registrar gasto"}</h3>
              <button onClick={() => setShowModal(false)} className="p-2 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Evento (liga este gasto a sus finanzas)</label>
                <select value={form.evento_id || ""} onChange={(e) => setForm({ ...form, evento_id: e.target.value || null })} className="eo-input" data-testid="g-evento">
                  <option value="">— Gasto general (sin evento) —</option>
                  {eventos.map(ev => <option key={ev.evento_id} value={ev.evento_id}>{ev.nombre || ev.tipo} · {ev.fecha || "s/f"}</option>)}
                </select>
              </div>
              <Field label="Descripción *" value={form.descripcion} onChange={(v) => setForm({ ...form, descripcion: v })} testid="g-descripcion" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Categoría</label>
                  <select value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className="eo-input" data-testid="g-categoria">
                    {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <Field label="Monto *" type="number" value={form.monto} onChange={(v) => setForm({ ...form, monto: parseFloat(v) || 0 })} testid="g-monto" />
              </div>
              <Field label="Proveedor" value={form.proveedor} onChange={(v) => setForm({ ...form, proveedor: v })} testid="g-proveedor" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Fecha" type="date" value={form.fecha} onChange={(v) => setForm({ ...form, fecha: v })} testid="g-fecha" />
                <div>
                  <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Método</label>
                  <select value={form.metodo_pago} onChange={(e) => setForm({ ...form, metodo_pago: e.target.value })} className="eo-input">
                    <option value="efectivo">Efectivo</option>
                    <option value="transferencia">Transferencia</option>
                    <option value="tarjeta">Tarjeta</option>
                  </select>
                </div>
              </div>
              <Field label="Referencia" value={form.referencia} onChange={(v) => setForm({ ...form, referencia: v })} testid="g-ref" />
            </div>
            <div className="p-4 flex gap-2 justify-end" style={{ borderTop: "1px solid var(--brand-border)" }}>
              <button onClick={() => setShowModal(false)} className="eo-btn eo-btn-outline">Cancelar</button>
              <button onClick={guardar} className="eo-btn eo-btn-primary" data-testid="g-guardar">{editando ? "Actualizar" : "Registrar"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Kpi = ({ label, value, icon: Icon, bg, color }) => (
  <div className="eo-card p-4 flex items-center gap-3">
    <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: bg }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--brand-text-muted)" }}>{label}</p>
      <p className="font-display text-xl truncate" style={{ color: "var(--brand-text)" }}>{value}</p>
    </div>
  </div>
);

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>{label}</label>
    <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="eo-input" data-testid={testid} />
  </div>
);

export default Finanzas;
